import { createSlice, type PayloadAction } from '@reduxjs/toolkit';
import { defaultMeasurementSystem, type MeasurementSystem } from 'helper/locale';
import { type DatePattern, datePatternFor, type TimePattern, timePatternFor } from 'helper/time';
import type { RootState } from 'store/reducers';

interface UserSettingsState {
    /**
     * Single switch for every regional presentation choice: `'us'` gives imperial units and
     * `Apr 13, 2026` dates, `'eu'` gives metric units and `13/04/2026` dates. Units and dates are
     * derived from this below rather than stored separately, so they cannot disagree.
     */
    measurementSystem: MeasurementSystem;
}

const initialState: UserSettingsState = {
    // Locale-derived, and applies on the first load only; redux-persist restores the operator's
    // choice on every later load.
    measurementSystem: defaultMeasurementSystem(),
};

export const userSettingsSlice = createSlice({
    name: 'userSettings',
    initialState,
    reducers: {
        setMeasurementSystem: (state: UserSettingsState, action: PayloadAction<MeasurementSystem>) => {
            state.measurementSystem = action.payload;
        },
    },
});

export const { setMeasurementSystem } = userSettingsSlice.actions;

/** Falls back to the locale default for stores persisted before this field existed. */
export const getMeasurementSystem = (state: RootState): MeasurementSystem =>
    state.userSettings.measurementSystem ?? defaultMeasurementSystem();

// Derived views on the single setting above. Consumers read these, never the raw system.
export const getUseImperialUnits = (state: RootState): boolean => getMeasurementSystem(state) === 'us';

export const getDatePattern = (state: RootState): DatePattern => datePatternFor(getMeasurementSystem(state));

export const getTimePattern = (state: RootState): TimePattern => timePatternFor(getMeasurementSystem(state));

/** For ticking readouts such as the header clock. */
export const getTimePatternWithSeconds = (state: RootState): TimePattern =>
    timePatternFor(getMeasurementSystem(state), true);

export default userSettingsSlice.reducer;
